// @ts-strict-ignore
import * as React from 'react';
import { useParams } from 'react-router-dom';
import { AppPlatform, AppStoreType, CompletedReleaseSummary, Integration, ReleaseType } from '../../types';
import { RollbackTag } from '@/components/appHome/RollbackTag';
import { AppComponentProps, AppRouteComponentMatchParams } from '../../typescriptHelpers';
import { ReleasePilotWidget } from '@/components/appHome/widgets/ReleasePilotWidget';
import { TargetDatesWidget } from '@/components/appHome/widgets/TargetDatesWidget';
import { AppHomeCard } from '@/components/appHome/AppHomeCard';
import Tag from 'antd/lib/tag';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import { Divider, Space } from 'antd/lib';
import { BuildSelectorComponent } from '@/components/sharedComponents/buildSelector/BuildSelectorComponent';
import { getStabilityInfos, stabilityIssuesSupportedForIntegration, statusColorForReleaseStatus } from '@/helpers';
import { AppHomeStabilityIssuesSection } from '@/components/appHome/widgets/AppHomeStabilityIssuesSection';
import { FeatureCompletionWidget } from '@/components/appHome/widgets/FeatureCompletionWidget';
import { MonitoringMetricsWidget } from '@/components/appHome/widgets/MonitoringMetricsWidget';
import { ReleaseSummaryWidget } from '@/components/appHome/widgets/ReleaseSummaryWidget';
import { statusStringForReleaseStatus } from '@/helpers/displayHelpers';
import { ReleaseMetricsWidget } from '@/components/appHome/widgets/ReleaseMetricsWidget';
import { CollapsibleSection } from '../monitoring/CollapsibleSection';
import RatingsReviews from '../RatingsReviews';

interface PastReleaseSummaryProps extends AppComponentProps {
    summary: CompletedReleaseSummary;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    stabilityIntegration: Integration;
    appStoreIntegration: Integration;
}

export const PastReleaseComponent: React.FC<PastReleaseSummaryProps> = (props) => {
    const { orgIdParam, appIdParam } = useParams<AppRouteComponentMatchParams>();

    const appStoreSelectedBuilds = props.summary.appStoreBuilds;
    const [selectedBuild, setSelectedBuild] = React.useState(
        props.summary.appStoreBuilds?.length > 0 ? props.summary.appStoreBuilds[0] : null,
    );

    const handleBuildSelect = (value) => {
        const matchedBuild = appStoreSelectedBuilds.find((build) => build.appStoreBuild.id === value);
        setSelectedBuild(matchedBuild);
    };

    const stabilityInfosWithIssuesSupport = getStabilityInfos(selectedBuild).filter((info) =>
        stabilityIssuesSupportedForIntegration(info.integrationId),
    );
    const showStabilityIssues = stabilityInfosWithIssuesSupport.length > 0;

    return (
        <AppHomeCard
            appId={appIdParam}
            orgId={orgIdParam}
            releaseId={props.summary.releaseId}
            id={props.summary.id}
            displayableVersion={props.summary.displayableVersion}
            releaseName={props.summary.releaseName}
            headerComponent={
                <>
                    <Space
                        align="center"
                        split={<Divider type="vertical" style={{ minHeight: 26, backgroundColor: antGrey.grey5 }} />}
                        wrap
                    >
                        <Tag
                            style={{
                                ...titleStyle,
                                fontSize: 11,
                                lineHeight: '22px',
                                color: runwayColors.white,
                            }}
                            color={statusColorForReleaseStatus(props.summary.status)}
                        >
                            {statusStringForReleaseStatus(props.summary.status)}
                        </Tag>

                        {props.summary.type === ReleaseType.Rollback && <RollbackTag />}

                        {selectedBuild && (
                            <BuildSelectorComponent
                                appStoreSelectedBuilds={appStoreSelectedBuilds}
                                selectedBuild={selectedBuild}
                                handleBuildSelect={handleBuildSelect}
                            />
                        )}
                    </Space>
                </>
            }
            contentComponent={
                <>
                    <ReleaseSummaryWidget summaryMessage={props.summary.releaseSummaryMessage} />
                    <ReleaseMetricsWidget
                        orgId={orgIdParam}
                        appId={appIdParam}
                        releaseId={props.summary.releaseId}
                        stats={props.summary.stats}
                    />
                    <MonitoringMetricsWidget orgId={orgIdParam} appId={appIdParam} summary={props.summary} />
                    <AppHomeStabilityIssuesSection
                        appId={props.appId}
                        stabilityInfosWithIssuesSupport={stabilityInfosWithIssuesSupport}
                        showStabilityIssues={showStabilityIssues}
                        stabilityIntegrationId={props.stabilityIntegration?.id}
                    />
                    {props.summary.appStoreReviewData && (
                        <div style={{ marginTop: 16 }}>
                            <CollapsibleSection title="Reviews" integrationType={props.appStoreIntegration?.id}>
                                <RatingsReviews
                                    reviewData={props.summary.appStoreReviewData}
                                    orgId={props.orgId}
                                    appId={props.appId}
                                />
                            </CollapsibleSection>
                        </div>
                    )}
                </>
            }
            sideComponent={
                <>
                    <TargetDatesWidget appId={appIdParam} summary={props.summary} />
                    <FeatureCompletionWidget featuresStep={props.summary.featuresStep} />
                    <Divider
                        style={{
                            margin: '16px 0',
                        }}
                    />
                    <ReleasePilotWidget
                        appId={appIdParam}
                        releasePilotId={props.summary.releasePilotId}
                        onReleaseOverview={false}
                    />
                </>
            }
        />
    );
};
